import { useEffect, useState, useCallback } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { api, money, fmtDate } from "@/lib/api";
import { useTenant } from "@/context/TenantContext";
import { useAuth } from "@/context/AuthContext";
import { MODULES } from "@/config/modules";
import StatusBadge from "@/components/StatusBadge";
import { RecordForm } from "@/components/DataModule";
import { WRITE_ROLES } from "@/components/JobDetail";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { toast } from "sonner";
import { Plus, Loader2, FolderKanban, ListChecks, Wrench, Trash2, Link2, Unlink, Pencil } from "lucide-react";

const P_FIELDS = MODULES.projects.fields;
const PHASE_STATUSES = ["Not Started", "In Progress", "Complete"];

function ProgressBar({ pct, tone = "accent" }) {
  const v = Math.max(0, Math.min(100, pct || 0));
  const color = tone === "red" ? "#DC2626" : tone === "amber" ? "#D97706" : "var(--client-accent)";
  return (
    <div className="h-2 rounded-full bg-slate-100 overflow-hidden w-full">
      <div className="h-full rounded-full transition-all" style={{ width: `${v}%`, backgroundColor: color }} />
    </div>
  );
}

// ---------------------------------------------------------------------------
// Phases tab
// ---------------------------------------------------------------------------
function Phases({ project, canWrite, onSaved }) {
  const [phases, setPhases] = useState(project.phases || []);
  const [newName, setNewName] = useState("");
  const [saving, setSaving] = useState(false);
  useEffect(() => { setPhases(project.phases || []); }, [project.id, project.phases]);

  const upd = (i, k, v) => setPhases((p) => p.map((ph, idx) => (idx === i ? { ...ph, [k]: v } : ph)));
  const add = () => {
    if (!newName.trim()) return;
    setPhases((p) => [...p, { name: newName.trim(), status: "Not Started", start_date: null, end_date: null, notes: "" }]);
    setNewName("");
  };
  const save = async () => {
    setSaving(true);
    try {
      await api.put(`/projects/${project.id}/phases`, { phases });
      toast.success("Phases saved — progress updated");
      onSaved();
    } catch (e) { toast.error(e.response?.data?.detail || "Save failed"); } finally { setSaving(false); }
  };

  return (
    <div className="space-y-3" data-testid="project-phases">
      {phases.length === 0 && <p className="text-sm text-slate-400 text-center py-4 border border-dashed border-slate-200 rounded-md">No phases yet — break the project into stages below.</p>}
      {phases.map((ph, i) => (
        <div key={ph.id || i} data-testid={`phase-row-${i}`} className="border border-slate-200 rounded-md p-3 space-y-2">
          <div className="flex items-center gap-2">
            <span className={`h-2.5 w-2.5 rounded-full flex-shrink-0 ${ph.status === "Complete" ? "bg-emerald-500" : ph.status === "In Progress" ? "bg-amber-500" : "bg-slate-300"}`} />
            {canWrite ? (
              <Input value={ph.name} onChange={(e) => upd(i, "name", e.target.value)} className="h-8 font-medium" data-testid={`phase-name-${i}`} />
            ) : <span className="font-medium text-sm text-slate-800">{ph.name}</span>}
            {canWrite && (
              <>
                <Select value={ph.status} onValueChange={(v) => upd(i, "status", v)}>
                  <SelectTrigger data-testid={`phase-status-${i}`} className="h-8 w-[135px] bg-white flex-shrink-0"><SelectValue /></SelectTrigger>
                  <SelectContent className="bg-white">{PHASE_STATUSES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
                </Select>
                <button data-testid={`phase-delete-${i}`} onClick={() => setPhases((p) => p.filter((_, idx) => idx !== i))} className="text-slate-300 hover:text-red-600 flex-shrink-0"><Trash2 className="h-4 w-4" /></button>
              </>
            )}
            {!canWrite && <StatusBadge status={ph.status === "Complete" ? "Completed" : ph.status} />}
          </div>
          {canWrite && (
            <div className="grid grid-cols-2 md:grid-cols-3 gap-2 pl-4">
              <Input type="date" value={(ph.start_date || "").slice(0, 10)} onChange={(e) => upd(i, "start_date", e.target.value)} className="h-8 text-xs" title="Start date" />
              <Input type="date" value={(ph.end_date || "").slice(0, 10)} onChange={(e) => upd(i, "end_date", e.target.value)} className="h-8 text-xs" title="End date" />
              <Input value={ph.notes || ""} onChange={(e) => upd(i, "notes", e.target.value)} placeholder="Notes" className="h-8 text-xs md:col-span-1 col-span-2" />
            </div>
          )}
          {!canWrite && (ph.notes || ph.start_date) && (
            <p className="text-xs text-slate-500 pl-4">{[ph.start_date && `From ${fmtDate(ph.start_date)}`, ph.end_date && `to ${fmtDate(ph.end_date)}`, ph.notes].filter(Boolean).join(" · ")}</p>
          )}
        </div>
      ))}
      {canWrite && (
        <div className="flex gap-2 border-t border-slate-100 pt-3">
          <Input data-testid="phase-new-input" value={newName} onChange={(e) => setNewName(e.target.value)} placeholder="Add a phase, e.g. Commissioning & handover" onKeyDown={(e) => e.key === "Enter" && add()} />
          <Button variant="outline" size="sm" className="flex-shrink-0" data-testid="phase-add-btn" onClick={add}><Plus className="h-4 w-4 mr-1" />Add phase</Button>
          <Button size="sm" className="btn-accent flex-shrink-0" data-testid="phase-save-btn" onClick={save} disabled={saving}>{saving && <Loader2 className="h-4 w-4 mr-1 animate-spin" />}Save phases</Button>
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Jobs tab
// ---------------------------------------------------------------------------
function ProjectJobs({ project, jobs, canWrite, cur, onChanged }) {
  const [allJobs, setAllJobs] = useState([]);
  const [linkId, setLinkId] = useState("");
  const [newTitle, setNewTitle] = useState("");
  const [busy, setBusy] = useState(false);
  useEffect(() => { if (canWrite) api.get("/jobs").then((r) => setAllJobs(r.data)); }, [canWrite]);

  const linkable = allJobs.filter((j) => !j.project_id && !["Closed", "Cancelled"].includes(j.operational_status));

  const link = async () => {
    if (!linkId) return;
    setBusy(true);
    try { await api.post(`/projects/${project.id}/link-job`, { job_id: linkId }); toast.success("Job linked to project"); setLinkId(""); onChanged(); }
    catch (e) { toast.error(e.response?.data?.detail || "Failed"); } finally { setBusy(false); }
  };
  const unlink = async (j) => {
    try { await api.post(`/projects/${project.id}/unlink-job`, { job_id: j.id }); toast.success(`${j.ref} unlinked`); onChanged(); }
    catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };
  const quickCreate = async () => {
    if (!newTitle.trim()) return toast.error("Give the job a title");
    setBusy(true);
    try {
      await api.post("/jobs", {
        title: newTitle.trim(), job_type: "Project work", priority: "Medium",
        customer_id: project.customer_id, customer_name: project.customer_name,
        site_id: project.site_id, site_name: project.site_name,
        description: `${newTitle.trim()} — part of project ${project.ref}.`,
        project_id: project.id, project_ref: project.ref, project_name: project.name,
        operational_status: "Ready to Schedule",
      });
      toast.success("Job created & linked — find it in Unscheduled to book it");
      setNewTitle(""); onChanged();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); } finally { setBusy(false); }
  };

  return (
    <div className="space-y-3" data-testid="project-jobs">
      {canWrite && (
        <div className="grid md:grid-cols-2 gap-3">
          <div className="border border-slate-200 rounded-md p-3">
            <p className="text-xs font-medium text-slate-600 mb-2 flex items-center gap-1"><Link2 className="h-3.5 w-3.5" />Link an existing job</p>
            <div className="flex gap-2">
              <Select value={linkId} onValueChange={setLinkId}>
                <SelectTrigger data-testid="project-link-select" className="bg-white h-9"><SelectValue placeholder="Choose job…" /></SelectTrigger>
                <SelectContent className="bg-white max-h-64">{linkable.map((j) => <SelectItem key={j.id} value={j.id}>{j.ref} · {j.title?.slice(0, 40)}</SelectItem>)}</SelectContent>
              </Select>
              <Button variant="outline" size="sm" className="h-9 flex-shrink-0" data-testid="project-link-btn" onClick={link} disabled={busy || !linkId}>Link</Button>
            </div>
          </div>
          <div className="border border-slate-200 rounded-md p-3">
            <p className="text-xs font-medium text-slate-600 mb-2 flex items-center gap-1"><Plus className="h-3.5 w-3.5" />New job in this project</p>
            <div className="flex gap-2">
              <Input data-testid="project-newjob-title" value={newTitle} onChange={(e) => setNewTitle(e.target.value)} placeholder="Job title…" className="h-9" onKeyDown={(e) => e.key === "Enter" && quickCreate()} />
              <Button size="sm" className="btn-accent h-9 flex-shrink-0" data-testid="project-newjob-btn" onClick={quickCreate} disabled={busy}>Create</Button>
            </div>
          </div>
        </div>
      )}
      <div className="border border-slate-200 rounded-md overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-slate-50"><tr>{["Job", "Scheduled", "Engineer", "Status", "Value", ""].map((h) => <th key={h} className="text-left font-medium text-slate-500 px-3 py-2 text-xs uppercase tracking-wide">{h}</th>)}</tr></thead>
          <tbody>
            {jobs.length === 0 && <tr><td colSpan={6} className="text-center py-6 text-slate-400">No jobs linked yet.</td></tr>}
            {jobs.map((j) => (
              <tr key={j.id} className="border-t border-slate-100" data-testid={`project-job-${j.id}`}>
                <td className="px-3 py-2"><span className="font-medium accent-text">{j.ref}</span> <span className="text-slate-700">{j.title}</span></td>
                <td className="px-3 py-2 text-slate-500 whitespace-nowrap">{fmtDate(j.scheduled_date)}</td>
                <td className="px-3 py-2 text-slate-500 whitespace-nowrap">{j.technician_name || "—"}</td>
                <td className="px-3 py-2"><StatusBadge status={j.operational_status} /></td>
                <td className="px-3 py-2 whitespace-nowrap">{money(j.expected_value, cur)}</td>
                <td className="px-3 py-2 text-right">
                  {canWrite && <button data-testid={`project-unlink-${j.id}`} onClick={() => unlink(j)} title="Unlink from project" className="text-slate-300 hover:text-red-600"><Unlink className="h-4 w-4" /></button>}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Project detail dialog
// ---------------------------------------------------------------------------
function ProjectDetail({ pid, onClose, onChanged, cur, canWrite }) {
  const [data, setData] = useState(null);
  const [editForm, setEditForm] = useState({});
  const [saving, setSaving] = useState(false);
  const load = useCallback(() => api.get(`/projects/${pid}/overview`).then((r) => { setData(r.data); setEditForm(r.data.project); }), [pid]);
  useEffect(() => { load(); }, [load]);

  const saveEdit = async () => {
    setSaving(true);
    try {
      const { phases, ...body } = editForm;
      await api.put(`/projects/${pid}`, body);
      toast.success("Project updated"); load(); onChanged();
    } catch (e) { toast.error(e.response?.data?.detail || "Save failed"); } finally { setSaving(false); }
  };

  const p = data?.project;
  const s = data?.stats;
  return (
    <Dialog open onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="bg-white max-w-3xl max-h-[88vh] overflow-y-auto" data-testid="project-detail-dialog">
        {!data ? <div className="py-10 text-center"><Loader2 className="h-6 w-6 animate-spin inline text-slate-400" /></div> : (
          <>
            <DialogHeader>
              <DialogTitle className="font-head flex items-center gap-2 flex-wrap">
                {p.ref} · {p.name} <StatusBadge status={p.status} />
              </DialogTitle>
              <DialogDescription>{p.customer_name}{p.site_name ? ` · ${p.site_name}` : ""}{p.project_manager ? ` · PM: ${p.project_manager}` : ""}
                {p.contract_ref && <> · <Link to={`/app/contracts?open=${p.contract_id}`} data-testid="project-contract-link" className="accent-text hover:underline">Contract {p.contract_ref}</Link></>}
              </DialogDescription>
            </DialogHeader>

            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              {[["Contract Value", money(s.contract_value, cur), null],
                ["Budgeted Cost", money(s.budget_cost, cur), null],
                ["Actual Cost", money(s.actual_cost, cur), s.budget_used_pct],
                ["Projected Margin", money(s.margin, cur), null]].map(([l, v, pct]) => (
                <div key={l} className="bg-slate-50 rounded-md p-3">
                  <div className="text-xs text-slate-500">{l}</div>
                  <div className={`text-lg font-head font-bold mt-0.5 ${l === "Projected Margin" ? (s.margin >= 0 ? "text-emerald-600" : "text-red-600") : "text-slate-900"}`}>{v}</div>
                  {pct !== null && pct !== undefined && (
                    <div className="mt-1.5">
                      <ProgressBar pct={pct} tone={pct > 100 ? "red" : pct > 80 ? "amber" : "accent"} />
                      <div className="text-[10px] text-slate-400 mt-0.5">{pct}% of budget used</div>
                    </div>
                  )}
                </div>
              ))}
            </div>
            <div className="flex items-center gap-3">
              <div className="flex-1"><ProgressBar pct={p.percent_complete} /></div>
              <span className="text-sm font-semibold text-slate-700" data-testid="project-progress">{p.percent_complete || 0}% complete</span>
              <span className="text-xs text-slate-400">{s.phases_complete}/{s.phases_total} phases · {s.jobs_completed}/{s.jobs_total} jobs done</span>
            </div>

            <Tabs defaultValue="phases">
              <TabsList>
                <TabsTrigger value="phases" data-testid="project-tab-phases"><ListChecks className="h-3.5 w-3.5 mr-1" />Phases ({s.phases_total})</TabsTrigger>
                <TabsTrigger value="jobs" data-testid="project-tab-jobs"><Wrench className="h-3.5 w-3.5 mr-1" />Jobs ({s.jobs_total})</TabsTrigger>
                <TabsTrigger value="edit" data-testid="project-tab-edit"><Pencil className="h-3.5 w-3.5 mr-1" />Details</TabsTrigger>
              </TabsList>
              <TabsContent value="phases" className="mt-4">
                <Phases project={p} canWrite={canWrite} onSaved={() => { load(); onChanged(); }} />
              </TabsContent>
              <TabsContent value="jobs" className="mt-4">
                <ProjectJobs project={p} jobs={data.jobs} canWrite={canWrite} cur={cur} onChanged={() => { load(); onChanged(); }} />
              </TabsContent>
              <TabsContent value="edit" className="mt-4">
                {p.scope && <p className="text-sm text-slate-600 whitespace-pre-wrap mb-3 bg-slate-50 rounded-md p-3">{p.scope}</p>}
                {canWrite ? (
                  <>
                    <RecordForm fields={P_FIELDS} value={editForm} onChange={setEditForm} />
                    <Button className="btn-accent mt-3" data-testid="project-edit-save" onClick={saveEdit} disabled={saving}>{saving && <Loader2 className="h-4 w-4 mr-1 animate-spin" />}Save changes</Button>
                  </>
                ) : <p className="text-sm text-slate-400">Read-only access.</p>}
              </TabsContent>
            </Tabs>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}

// ---------------------------------------------------------------------------
// Projects page
// ---------------------------------------------------------------------------
export default function Projects() {
  const { company } = useTenant();
  const { user } = useAuth();
  const cur = company?.currency || "EUR";
  const canWrite = WRITE_ROLES.includes(user?.role);
  const [rows, setRows] = useState(null);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({});
  const [saving, setSaving] = useState(false);
  const [sel, setSel] = useState(null);

  const load = useCallback(() => api.get("/projects").then((r) => setRows(r.data)).catch(() => setRows([])), []);
  useEffect(() => { load(); }, [load]);

  const [searchParams, setSearchParams] = useSearchParams();
  useEffect(() => {
    const openId = searchParams.get("open");
    if (openId && rows) {
      const p = rows.find((x) => x.id === openId);
      if (p) setSel(p);
      else toast.error("That project no longer exists");
      setSearchParams({}, { replace: true });
    }
  }, [rows, searchParams, setSearchParams]);

  const create = async () => {
    if (!form.name) return toast.error("Project name required");
    setSaving(true);
    try {
      const { data } = await api.post("/projects", form);
      toast.success(`Project ${data.ref} created`); setOpen(false); load(); setSel(data);
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); } finally { setSaving(false); }
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-4 gap-3 flex-wrap">
        <div>
          <h1 className="text-2xl font-head font-bold text-slate-900 flex items-center gap-2"><FolderKanban className="h-6 w-6 text-slate-400" />Projects</h1>
          <p className="text-sm text-slate-500">Larger installation works — phases, budgets and linked jobs. {rows ? `${rows.length} project(s)` : ""}</p>
        </div>
        {canWrite && <Button className="btn-accent" data-testid="project-new-btn" onClick={() => { setForm({ status: "Planning" }); setOpen(true); }}><Plus className="h-4 w-4 mr-1" />New Project</Button>}
      </div>

      <div className="bg-white border border-slate-200 rounded-md overflow-x-auto scroll-thin">
        <table className="w-full text-sm">
          <thead className="bg-slate-50"><tr>{["Ref", "Project", "Customer", "PM", "Progress", "Value", "Target", "Status"].map((h) => <th key={h} className="text-left font-medium text-slate-500 px-3 py-2.5 whitespace-nowrap">{h}</th>)}</tr></thead>
          <tbody>
            {rows === null && <tr><td colSpan={8} className="text-center py-10 text-slate-400"><Loader2 className="h-5 w-5 animate-spin inline" /></td></tr>}
            {rows && rows.length === 0 && <tr><td colSpan={8} className="text-center py-10 text-slate-400">No projects yet. Create one to manage larger installation works.</td></tr>}
            {rows && rows.map((p) => (
              <tr key={p.id} data-testid={`project-row-${p.id}`} className="data-row border-t border-slate-100 cursor-pointer" onClick={() => setSel(p)}>
                <td className="px-3 py-2.5 font-medium accent-text whitespace-nowrap">{p.ref}</td>
                <td className="px-3 py-2.5 text-slate-800 font-medium max-w-[260px] truncate">{p.name}</td>
                <td className="px-3 py-2.5 text-slate-600 whitespace-nowrap">{p.customer_name || "—"}</td>
                <td className="px-3 py-2.5 text-slate-600 whitespace-nowrap">{p.project_manager || "—"}</td>
                <td className="px-3 py-2.5 w-40">
                  <div className="flex items-center gap-2"><ProgressBar pct={p.percent_complete} /><span className="text-xs text-slate-500 w-9">{p.percent_complete || 0}%</span></div>
                </td>
                <td className="px-3 py-2.5 whitespace-nowrap">{money(p.contract_value, cur)}</td>
                <td className="px-3 py-2.5 text-slate-500 whitespace-nowrap">{fmtDate(p.target_completion_date)}</td>
                <td className="px-3 py-2.5"><StatusBadge status={p.status} /></td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent onInteractOutside={(e) => e.preventDefault()} className="bg-white max-w-2xl max-h-[85vh] overflow-y-auto">
          <DialogHeader><DialogTitle className="font-head">New Project</DialogTitle>
            <DialogDescription>Phases and jobs can be added once the project is created.</DialogDescription></DialogHeader>
          <RecordForm fields={P_FIELDS} value={form} onChange={setForm} />
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
            <Button className="btn-accent" data-testid="project-create-save" onClick={create} disabled={saving}>{saving && <Loader2 className="h-4 w-4 mr-1 animate-spin" />}Create Project</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {sel && <ProjectDetail pid={sel.id} onClose={() => setSel(null)} onChanged={load} cur={cur} canWrite={canWrite} />}
    </div>
  );
}
